import React, { useEffect } from 'react';
import { ArrowLeft, Eye } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

/**
 * ImpersonationBanner — always-on-top red bar shown ONLY while the master
 * admin is impersonating a customer. The big back arrow on the LEFT exits
 * impersonation in one click (restores the admin's original token).
 *
 * Adds `body.imp-on` while active so global CSS can shift the sticky
 * Navbar (and any other top-anchored chrome) down by the banner height —
 * otherwise the fixed banner would overlap the navbar.
 *
 * Renders nothing when not impersonating.
 */
const BANNER_HEIGHT_PX = 36;

const ImpersonationBanner = () => {
  const { user, isImpersonating, stopImpersonation } = useAuth();
  const active = isImpersonating && isImpersonating();

  useEffect(() => {
    if (!active) return;
    document.body.classList.add('imp-on');
    return () => {
      document.body.classList.remove('imp-on');
    };
  }, [active]);

  if (!active) return null;

  const customerLabel = user?.name || user?.company_name || user?.customer_id || 'customer';
  const customerMeta = [user?.customer_id, user?.mobile].filter(Boolean).join(' · ');

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[200] bg-red-600 text-white shadow-md"
      style={{ height: `${BANNER_HEIGHT_PX}px` }}
      data-testid="impersonation-banner"
    >
      <div className="max-w-6xl mx-auto px-3 h-full flex items-center gap-3">
        <button
          onClick={stopImpersonation}
          className="inline-flex items-center gap-1.5 rounded-full bg-white text-red-700 px-2.5 py-1 text-[12px] font-bold shadow hover:bg-red-50 transition-colors"
          title="Exit and return to your admin account"
          data-testid="impersonation-exit-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <Eye className="w-4 h-4 opacity-80 hidden sm:block" />
        <div className="text-[12px] sm:text-[13px] font-medium truncate">
          Viewing as <span className="font-bold">{customerLabel}</span>
          <span className="hidden sm:inline opacity-80"> — {customerMeta}</span>
        </div>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
